export async function simulateApiFail(delay = 1000) {
  await new Promise((resolve) => setTimeout(resolve, delay));
  throw new Error('Simulated failure');
}

const SANITIZED_SENTINEL = '__SANITIZED__';
/** -Infinity before Infinity so array patterns do not partially match. */
const INVALID_JSON_VALUES = ['-Infinity', 'Infinity', 'NaN'] as const;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export interface SanitizedEntry {
  path: string;
  fund_name?: string;
}

export function sanitizeNaNInfinity(jsonString: string): {
  data: unknown;
  wasSanitized: boolean;
  sanitizedEntries: SanitizedEntry[];
} {
  const replaced = INVALID_JSON_VALUES.reduce((str, val) => {
    const token = escapeRegExp(val);
    const quoted = `"${SANITIZED_SENTINEL}${val}"`;
    return str
      .replace(new RegExp(`:\\s*${token}`, 'g'), `: ${quoted}`)
      .replace(new RegExp(`,\\s*${token}`, 'g'), `, ${quoted}`)
      .replace(new RegExp(`\\[\\s*${token}`, 'g'), `[${quoted}`);
  }, jsonString);

  const sanitizedEntries: SanitizedEntry[] = [];

  /**
   * Recursively traverses parsed JSON, replacing sentinel strings with null.
   * Builds dot/bracket paths (e.g. `[0].monitor_data.series[2][1]`) for each
   * sanitized value and attaches `fund_name` from the nearest ancestor object.
   */
  const walk = (
    value: unknown,
    path: string,
    ancestors: Record<string, unknown>[] = [],
  ): unknown => {
    // Leaf: sentinel string → record path, swap to null.
    if (typeof value === 'string' && value.startsWith(SANITIZED_SENTINEL)) {
      const fund_name = [...ancestors]
        .reverse()
        .find((o) => typeof o.fund_name === 'string')?.fund_name as
        | string
        | undefined;
      sanitizedEntries.push({
        path: path || '(root)',
        ...(fund_name && { fund_name }),
      });
      return null;
    }

    // Arrays: descend with index-based paths.
    if (Array.isArray(value)) {
      return value.map((v, i) =>
        walk(v, path ? `${path}[${i}]` : `[${i}]`, ancestors),
      );
    }

    // Objects: descend with dot-separated keys, tracking ancestors for fund_name lookup.
    if (value && typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [
          k,
          walk(v, path ? `${path}.${k}` : k, [...ancestors, obj]),
        ]),
      );
    }

    return value;
  };

  const data = walk(JSON.parse(replaced), '');

  return {
    data,
    wasSanitized: replaced !== jsonString,
    sanitizedEntries,
  };
}
